import React, { useEffect } from 'react';
import { AdminPanel } from '@/components/AdminPanel';
import { Box, Typography, IconButton, CircularProgress, Alert } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentUserSettingsQuery } from '@/features/userSettings/hooks/useUserSettingsQuery';
import { queryKeys } from '@/lib/queryKeys';
import { QueryErrorState } from '@/components/state';

const AdminPanelPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    data: currentUserSettings,
    isPending: loading,
    isError: isUserSettingsError,
    error: userSettingsError,
  } = useCurrentUserSettingsQuery();

  const hasAccess =
    currentUserSettings?.role === 'manager' || currentUserSettings?.is_admin === true;
  const userRole: 'manager' | null =
    currentUserSettings?.role === 'manager'
      ? 'manager'
      : null;
  const isAdmin = currentUserSettings?.is_admin || false;

  useEffect(() => {
    if (loading) {
      return;
    }

    if (isUserSettingsError) {
      console.error('Error checking authorization:', userSettingsError);
      return;
    }

    if (!hasAccess) {
      const timeoutId = window.setTimeout(() => {
        navigate('/');
      }, 2000);

      return () => window.clearTimeout(timeoutId);
    }
  }, [loading, isUserSettingsError, userSettingsError, hasAccess, navigate]);

  const retryCurrentUserSettings = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.userSettings.current() });
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isUserSettingsError) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 3,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 560 }}>
          <QueryErrorState
            title="Unable to verify admin access"
            message={userSettingsError instanceof Error ? userSettingsError.message : 'Please try again.'}
            onRetry={() => {
              void retryCurrentUserSettings();
            }}
            minHeight={220}
          />
        </Box>
      </Box>
    );
  }

  if (!hasAccess) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography>
            You don't have permission to access the Admin Panel. Only managers or users with is_admin enabled can access this page.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            Redirecting to home page...
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        overflow: 'auto',
      }}
    >
      {/* Header with back button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          height: '45px',
          px: { xs: 0.5, sm: 1.5 },
          py: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <IconButton
          onClick={() => navigate('/')}
          sx={{
            color: 'primary.main',
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Admin Panel
        </Typography>
      </Box>

      {/* Content area */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 0 }}>
        <AdminPanel userRole={userRole} isAdmin={isAdmin} currentUserId={currentUserSettings?.id} />
      </Box>
    </Box>
  );
};

export default AdminPanelPage;
